'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import PropTypes from "prop-types";
import { mergeDeep } from '../foundation/utilities';
import { SmartImage } from '../elements/smartimage';
import { smartFetch } from '../foundation/smartfetch';
import { buildUrl } from '../foundation/urlbuilder';
import { usePixelatedConfig } from '../config/config.client';
import './socialcard.css';
/* ========== NOTES ==========
TODO #24 SocialCard Component - Add Blurb FaceBook Github iStock LinkedIn SnapChat ShutterStock TickTock Google News Saved Articles
ERRORS: 500px, shutterfly

/* function removeAnchors(element) {
    const anchors = element.querySelectorAll('a');
    anchors.forEach(anchor => {
        const parent = anchor.parentNode;
        const text = anchor.innerHTML;
        parent.insertBefore(document.createTextNode(text), anchor);
        parent.removeChild(anchor);
    });
} */
function removeDeadHrefs(element) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(element, 'text/html');
    const anchors = doc.querySelectorAll('a');
    anchors.forEach(anchor => {
        const href = anchor.getAttribute('href');
        if (href && !href.startsWith('http://') && !href.startsWith('https://')) {
            anchor.removeAttribute('href');
        }
    });
    return doc.body.innerHTML;
}
/* ========== SOCIALCARD ========== */
/**
 * SocialCards — Aggregate feeds from configured sources (RSS, APIs) and render as social cards.
 *
 * @param {object} [props.sources] - Map of source configurations (url/userID/entryCount/iconSrc/iconSrcAlt) keyed by source name.
 */
SocialCards.propTypes = {
    /** Map of feed source configurations (e.g., { instagram: { userID, entryCount }, blog: { url, entryCount }, ... }) */
    sources: PropTypes.object.isRequired
};
export function SocialCards(props) {
    const debug = false;
    // Get config values from provider
    const config = usePixelatedConfig();
    const proxyURL = config?.integrations?.global?.proxyUrl || 'https://proxy.pixelated.tech/prod/proxy';
    const [state, setState] = useState({
        loading: true,
        targetID: '#social',
        myCardData: [],
        mySocialCards: [],
        proxy: {
            proxyURL,
            proxyURLParam: 'url'
        },
        rss2json: {
            apiURL: 'https://api.rss2json.com/v1/api.json',
            apiURLParam: 'rss_url',
            apiKey: ''
        },
        toptal: {
            apiURL: 'https://www.toptal.com/developers/feed2json/convert',
            apiURLParam: 'url',
            apiKey: ''
        },
        sources: mergeDeep({
            /* DEFAULT VALUES FOR KNOWN SOURCES */
            blank: { url: null, entryCount: 5, iconSrc: '', iconSrcAlt: '' },
            SOOpx: { url: null, entryCount: 5, iconSrc: 'https://www.pixelated.tech/images/logos/500px-logo.png', iconSrcAlt: '500px Post' },
            blog: { url: null, entryCount: 5, iconSrc: 'https://www.pixelated.tech/images/logos/blog-logo.png', iconSrcAlt: 'Blog Post' },
            ebay: { url: null, entryCount: 5, iconSrc: 'https://www.pixelated.tech/images/logos/ebay-logo.png', iconSrcAlt: 'eBay Items For Sale' },
            etsy: { url: null, entryCount: 5, iconSrc: 'https://www.pixelated.tech/images/logos/etsy-logo.png', iconSrcAlt: 'Etsy Favorite' },
            /* facebook: { iconSrc: 'https://www.pixelated.tech/images/logos/facebook-logo.png', iconSrcAlt: 'Facebook Wall Post' }, */
            flickr: { userID: '', apiKey: '', tags: '', entryCount: 5, iconSrc: 'https://www.pixelated.tech/images/logos/flickr-logo.png', iconSrcAlt: 'Flickr Photo' },
            /* ==========
            NOTE - FourSquare RSS stopped working March 2019
            ========== */
            /* foursquare: { url: null , entryCount: 5, iconSrc: 'https://www.pixelated.tech/images/logos/foursquare-logo.png', iconSrcAlt: 'FourSquare Checkin' }, */
            github: { url: null, entryCount: 5, iconSrc: 'https://www.pixelated.tech/images/logos/github-logo.png', iconSrcAlt: 'Github Activity' },
            goodreads: { url: null, entryCount: 5, iconSrc: 'https://www.pixelated.tech/images/logos/goodreads-logo.png', iconSrcAlt: 'GoodReads Currently Reading' },
            instagram: { userID: '', entryCount: 5, iconSrc: 'https://www.pixelated.tech/images/logos/instagram-logo.jpg', iconSrcAlt: 'Instagram Photo' },
            pinterest: { url: null, entryCount: 5, iconSrc: 'https://www.pixelated.tech/images/logos/pinterest-logo.png', iconSrcAlt: 'Pinterest Pin' },
            reddit: { url: null, entryCount: 5, iconSrc: 'https://www.pixelated.tech/images/logos/reddit-logo.png', iconSrcAlt: 'Reddit Saves' },
            shutterfly: { url: null, entryCount: 5, iconSrc: 'https://www.pixelated.tech/images/logos/shutterfly-logo.jpg', iconSrcAlt: 'Shutterfly Items' },
            tumblr: { url: null, entryCount: 5, iconSrc: 'https://www.pixelated.tech/images/logos/tumblr-logo.png', iconSrcAlt: 'Tumblr Post' },
            twitter: { url: null, entryCount: 5, iconSrc: 'https://www.pixelated.tech/images/logos/twitter-logo.png', iconSrcAlt: 'Twitter Tweet' },
            x: { url: null, entryCount: 5, iconSrc: 'https://www.pixelated.tech/images/logos/x-logo.png', iconSrcAlt: 'X Post' },
            youtube: { url: null, entryCount: 5, iconSrc: 'https://www.pixelated.tech/images/logos/youtube-logo.png', iconSrcAlt: 'Youtube Favorite Video' },
            other: { url: null, entryCount: 5, iconSrc: 'https://www.pixelated.tech/images/logos/blog-logo.png', iconSrcAlt: 'Post' }
        }, props.sources)
    });
    async function RSSFeedToJson(url) {
        if (debug) {
            console.log('Fetching RSS...', url);
        }
        async function fetchRSS() {
            try {
                const text = await smartFetch(url, {
                    responseType: 'text',
                    requestInit: {
                        method: 'GET',
                        credentials: 'same-origin',
                        mode: 'cors',
                        headers: { 'Content-Type': 'application/json' }
                    }
                });
                const parser = new DOMParser();
                const xml = parser.parseFromString(text, 'application/xml');
                let items;
                if (xml.querySelectorAll('item').length > 0) {
                    items = Array.from(xml.querySelectorAll('item')).map(item => {
                        return {
                            author: item.querySelector('author')?.textContent,
                            category: item.querySelector('category')?.textContent,
                            description: item.querySelector('description')?.textContent,
                            // description: removeDeadAnchors(item.querySelector('description')?.textContent),
                            guid: item.querySelector('guid')?.textContent,
                            link: item.querySelector('link')?.textContent,
                            pubDate: item.querySelector('pubDate')?.textContent,
                            source: item.querySelector('source')?.textContent,
                            title: item.querySelector('title')?.textContent
                        };
                    });
                }
                else {
                    /* ===== FIX FOR REDDIT ===== */
                    items = Array.from(xml.querySelectorAll('entry')).map(item => {
                        return {
                            author: item.querySelector('author')?.textContent,
                            category: item.querySelector('category')?.attributes.getNamedItem('term')?.nodeValue,
                            description: item.querySelector('content')?.textContent,
                            // description: removeDeadAnchors(item.querySelector('content')?.textContent),
                            guid: item.querySelector('id')?.textContent,
                            link: item.querySelector('link')?.attributes.getNamedItem('href')?.nodeValue,
                            pubDate: item.querySelector('published')?.textContent,
                            source: item.querySelector('source')?.textContent,
                            title: item.querySelector('title')?.textContent
                        };
                    });
                }
                return (items);
            }
            catch (err) {
                return (err);
            }
        }
        return fetchRSS();
    }
    function sortCardsByPubDate(a, b) {
        const property = 'pubDate';
        const dateA = new Date(a[property]);
        const dateB = new Date(b[property]);
        if (dateA < dateB) {
            return 1;
        }
        else if (dateA > dateB) {
            return -1;
        }
        else {
            return 0;
        }
    }
    async function getFeedEntries(myURL, entryCount) {
        if (debug) {
            console.log('Getting Feed Entries... ', myURL);
        }
        const proxiedURL = buildUrl({
            baseUrl: myURL,
            proxyUrl: state.proxy.proxyURL,
        });
        let sourceCardData = [];
        // const result = await RSSFeedToJson(proxiedURL)
        await RSSFeedToJson(proxiedURL)
            .then((items) => {
            let i = 0;
            for (const prop in items) {
                let myNewCard;
                const item = items[prop];
                myNewCard = item;
                /* ===== FIX FOR SOURCE ===== */
                if (!(item.source)) {
                    myNewCard.source = new URL(myURL).hostname;
                }
                sourceCardData.push(myNewCard);
                // ===== BREAK ON ENTRY COUNT ===== */
                if (i >= entryCount - 1) {
                    break;
                }
                i++;
            }
        });
        return sourceCardData;
    }
    async function gatherData() {
        if (debug) {
            console.log('Gathering Data...');
        }
        let allCardData = [];
        for (const prop in state.sources) {
            const source = state.sources[prop];
            let sourceCardData;
            if (Object.prototype.hasOwnProperty.call(source, 'url') && source.url && source.url.length > 0) {
                sourceCardData = await getFeedEntries(source.url, source.entryCount);
                allCardData = [...allCardData, ...sourceCardData];
            }
        }
        if (debug) {
            console.log('All card Data... ', allCardData);
        }
        allCardData = allCardData.sort(sortCardsByPubDate);
        return allCardData;
    }
    useEffect(() => {
        if (debug) {
            console.log("Did Mount!");
        }
        let myNewCardData = [];
        let myNewSocialCards = [];
        const generateSocialCards = async () => {
            try {
                myNewCardData = await gatherData();
                for (const prop in myNewCardData) {
                    let myOptions = {};
                    const card = myNewCardData[prop];
                    switch (true) {
                        case (card.link?.indexOf('500px.com') > -1):
                            myOptions = state.sources.SOOpx;
                            break;
                        case (card.link?.indexOf('blog') > -1):
                            myOptions = state.sources.blog;
                            break;
                        case (card.link?.indexOf('ebay.com') > -1):
                            myOptions = state.sources.ebay;
                            break;
                        case (card.link?.indexOf('etsy.com') > -1):
                            myOptions = state.sources.etsy;
                            break;
                        case (card.link?.indexOf('flickr.com') > -1):
                            myOptions = state.sources.flickr;
                            break;
                        case (card.link?.indexOf('github.com') > -1):
                            myOptions = state.sources.github;
                            break;
                        case (card.link?.indexOf('goodreads.com') > -1):
                            myOptions = state.sources.goodreads;
                            break;
                        case (card.link?.indexOf('instagram') > -1):
                            myOptions = state.sources.instagram;
                            break;
                        case (card.link?.indexOf('pinterest.com') > -1):
                            myOptions = state.sources.pinterest;
                            break;
                        case (card.link?.indexOf('reddit.com') > -1):
                            myOptions = state.sources.reddit;
                            break;
                        case (card.link?.indexOf('shutterfly.com') > -1):
                            myOptions = state.sources.shutterfly;
                            break;
                        case (card.link?.indexOf('tumblr.com') > -1):
                            myOptions = state.sources.tumblr;
                            break;
                        case (card.link?.indexOf('twitter') > -1):
                            myOptions = state.sources.twitter;
                            break;
                        case (card.link?.indexOf('x.com') > -1):
                            myOptions = state.sources.x;
                            break;
                        case (card.link?.indexOf('youtube') > -1):
                            myOptions = state.sources.youtube;
                            break;
                        case (card.link?.indexOf('other') > -1):
                            myOptions = state.sources.other;
                            break;
                        default:
                            myOptions = state.sources.blank;
                            break;
                    }
                    /* ===== UPDATE STATE ===== */
                    const newSocialCard = _jsx(SocialCard, { iconSrc: myOptions.iconSrc, iconSrcAlt: myOptions.iconSrcAlt, card: card }, prop + '' + card.guid);
                    myNewSocialCards.push(newSocialCard);
                }
            }
            catch (e) {
                console.log("Error : ", e);
            }
            finally {
                setState({
                    ...state,
                    myCardData: myNewCardData,
                    mySocialCards: myNewSocialCards,
                    loading: false
                });
            }
        };
        generateSocialCards();
    }, [props.sources]);
    if (state.loading) {
        return (_jsx(SocialCardsLoading, {}));
    }
    else {
        return (state.mySocialCards);
    }
}
/**
 * SocialCard — Render a single social feed item as a card with icon, title, description, and date.
 *
 * @param {string} [props.iconSrc] - URL/path to the source icon image.
 * @param {string} [props.iconSrcAlt] - Alt text for the source icon.
 * @param {any} [props.card] - Feed item object containing title, link, description and pubDate.
 */
SocialCard.propTypes = {
    /** Source icon image URL */
    iconSrc: PropTypes.string.isRequired,
    /** Alt text for the icon */
    iconSrcAlt: PropTypes.string.isRequired,
    /** Feed item object with title/link/description/pubDate */
    card: PropTypes.any.isRequired
};
export function SocialCard(props) {
    const config = usePixelatedConfig();
    return (_jsx("div", { className: "masonry-item", children: _jsxs("div", { className: "card", children: [_jsx("div", { className: "card-title", children: _jsxs("a", { href: props.card.link, target: "_blank", rel: "noopener noreferrer", children: [_jsx(SmartImage, { className: "card-icon", src: props.iconSrc, title: props.iconSrcAlt, alt: props.iconSrcAlt, cloudinaryEnv: config?.integrations?.cloudinary?.product_env, cloudinaryDomain: config?.integrations?.cloudinary?.baseUrl, cloudinaryTransforms: config?.integrations?.cloudinary?.transforms }), props.card.title] }) }), _jsx("div", { className: "card-body", dangerouslySetInnerHTML: { __html: removeDeadHrefs(props.card.description) } }), _jsx("div", { className: "card-date", children: props.card.pubDate })] }) }, props.card.guid));
}
/* ========== SPINNER ========== */
/** SocialCardsLoading.propTypes — No props (simple loading indicator).
 * @param {any} [props] - No props are accepted by SocialCardsLoading.
 */
SocialCardsLoading.propTypes = { /** no props */};
export function SocialCardsLoading() {
    return (_jsx("div", { className: "cards-loading", children: _jsx("div", { children: "Loading..." }) }));
}
